import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  ITicket,
  ITicketComment,
  ITicketsSummary,
} from "@/types/(company)/help-and-support";

export const helpAndSupportApi = createApi({
  reducerPath: "helpAndSupportApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "supportTickets",
    "ticketComments",
    "getTicketById",
    "ticketsSummary",
  ],
  endpoints: (builder) => ({
    createTicket: builder.mutation<IApiResponse<void>, FormData>({
      query: (payload) => ({
        url: "/company/support-tickets",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["supportTickets", "ticketsSummary"],
    }),
    getTicketsSummary: builder.query<IApiResponse<ITicketsSummary>, void>({
      query: () => ({
        url: "/company/support-tickets/stats",
        method: "GET",
      }),
      providesTags: ["ticketsSummary"],
    }),
    getTickets: builder.query<
      IApiResponse<ITicket[]>,
      { page?: number; limit?: number; search?: string; status?: string; priority?: string; module?: string; category?: string; sortBy?: string; sortOrder?: string }
    >({
      query: (params) => ({
        url: "/company/support-tickets",
        method: "GET",
        params,
      }),
      providesTags: ["supportTickets"],
    }),
    getTicketById: builder.query<IApiResponse<ITicket>, string>({
      query: (id) => ({
        url: `/company/support-tickets/${id}`,
        method: "GET",
      }),
      providesTags: (_, __, id) => [{ type: "getTicketById", id: id }],
    }),
    getTicketComments: builder.query<IApiResponse<ITicketComment[]>, string>({
      query: (id) => ({
        url: `/company/support-tickets/${id}/comments`,
        method: "GET",
      }),
      providesTags: (_, __, id) => [{ type: "ticketComments", id }],
    }),
    createTicketComment: builder.mutation<
      IApiResponse<void>,
      { ticketId: number; content: string; parentCommentId?: number }
    >({
      query: ({ ticketId, content, parentCommentId }) => ({
        url: `/company/support-tickets/${ticketId}/comments`,
        method: "POST",
        body: { content, parentCommentId },
      }),
      invalidatesTags: (_, __, { ticketId }) => [
        { type: "ticketComments", ticketId },
      ],
    }),
    toggleCommentLike: builder.mutation<
      IApiResponse<void>,
      { ticketId: number; commentId: number }
    >({
      query: ({ commentId }) => ({
        url: `/company/support-tickets/comments/${commentId}/like`,
        method: "POST",
      }),
      invalidatesTags: (_, __, { ticketId }) => [
        { type: "ticketComments", id: ticketId },
      ],
    }),
    updateTicketStatus: builder.mutation<
      IApiResponse<void>,
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/company/support-tickets/${id}/change-status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "getTicketById", id: id },
        "supportTickets",
        "ticketsSummary",
      ],
    }),
  }),
});

export const {
  useCreateTicketMutation,
  useGetTicketsSummaryQuery,
  useGetTicketsQuery,
  useGetTicketByIdQuery,
  useGetTicketCommentsQuery,
  useCreateTicketCommentMutation,
  useUpdateTicketStatusMutation,
  useToggleCommentLikeMutation,
} = helpAndSupportApi;
